import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService, ConfirmEventType } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Project } from '../../models/project.model';
import { ProjectService } from '../../services/project.service';
import { WeekPlanTaskService } from '../../services/week-plan-task.service';
import { NextWeekPlanTaskService } from '../../services/next-week-plan-task.service';
import { MilestonePlanTaskService, MilestonePlanTask } from '../../services/milestone-plan-task.service';
import { RaidItemService, RaidItem } from '../../services/raid-item.service';
import { CurrentWeekPlanComponent } from '../current-week-plan/current-week-plan.component';
import { MilestonePlanComponent } from '../milestone-plan/milestone-plan.component';
import { ProjectTasksComponent } from '../project-tasks/project-tasks.component';
import { RaidComponent } from '../raid/raid.component';
import { ChangeLogComponent } from '../change-log/change-log.component';
import { PaymentTrackingComponent } from '../payment-tracking/payment-tracking.component';
import { DecisionLogComponent } from '../decision-log/decision-log.component';
import { NextWeekPlanComponent } from '../next-week-plan/next-week-plan.component';
import { ProjectChecklistComponent } from '../project-checklist/project-checklist.component';
import { TeamAllocationMonthComponent } from '../team-allocation-month/team-allocation-month.component';
import {
  Document, Packer, Paragraph, Table, TableRow, TableCell,
  TextRun, WidthType, BorderStyle, VerticalAlign
} from 'docx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-project-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Dialog,
    Button,
    InputText,
    Select,
    DatePicker,
    Tooltip,
    CurrentWeekPlanComponent,
    MilestonePlanComponent,
    ProjectTasksComponent,
    RaidComponent,
    ChangeLogComponent,
    PaymentTrackingComponent,
    DecisionLogComponent,
    NextWeekPlanComponent,
    ProjectChecklistComponent,
    TeamAllocationMonthComponent,
    ConfirmDialog
  ],
  templateUrl: './project-details.component.html',
  styleUrl: './project-details.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class ProjectDetailsComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @ViewChild(ProjectChecklistComponent) private checklistComponent?: ProjectChecklistComponent;

  @Input() project: Project | null = null;
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<void>();
  @Output() projectUpdated = new EventEmitter<Project>();

  editedProject: Project | null = null;
  editedStartDate: Date | null = null;
  editedEndDate: Date | null = null;
  isSaving: boolean = false;
  isLoading: boolean = false;
  activeTab: string = 'project-details';
  checklistHasUnsavedChanges = false;
  private pendingClose = false;

  statusOptions = [
    { label: 'Green', value: 'Green' },
    { label: 'Amber', value: 'Amber' },
    { label: 'Red', value: 'Red' }
  ];

  progressOptions = [
    { label: 'To Start', value: 'To Start' },
    { label: 'On track', value: 'On track' },
    { label: 'Off track', value: 'Off track' },
    { label: 'Blocker', value: 'Blocker' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Closed', value: 'Closed' }
  ];

  constructor(
    private projectService: ProjectService,
    private weekPlanTaskService: WeekPlanTaskService,
    private nextWeekPlanTaskService: NextWeekPlanTaskService,
    private milestonePlanTaskService: MilestonePlanTaskService,
    private raidItemService: RaidItemService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router
  ) {}

  ngOnInit() {
    this.initializeProject();
  }

  ngOnChanges(changes: SimpleChanges) {
    // When dialog becomes visible and project is available, fetch latest data
    if (changes['visible'] && this.visible && this.project?.id) {
      this.loadProjectDetails();
    } else if (changes['project'] && this.project && this.visible) {
      // If project changes while dialog is open, reload
      this.loadProjectDetails();
    } else if (changes['project'] && this.project && !this.visible) {
      // If project changes but dialog is not visible, just initialize
      this.initializeProject();
    }
  }

  initializeProject() {
    if (this.project) {
      // Create a copy for editing
      this.editedProject = {
        ...this.project
      };
      
      // Convert startDate string to Date object for date picker
      // Reset to null first to prevent auto-opening calendar
      this.editedStartDate = null;
      this.editedEndDate = null;
      
      // Set the dates after a small delay to prevent auto-opening
      // Fix timezone issue by parsing date string properly
      setTimeout(() => {
        if (this.project?.startDate) {
          const startDateParts = this.project.startDate.split('-');
          if (startDateParts.length === 3) {
            // Create date in local timezone to avoid one day less issue
            this.editedStartDate = new Date(parseInt(startDateParts[0]), parseInt(startDateParts[1]) - 1, parseInt(startDateParts[2]));
          } else {
            this.editedStartDate = new Date(this.project.startDate);
          }
        }
        
        if (this.project?.endDate) {
          const endDateParts = this.project.endDate.split('-');
          if (endDateParts.length === 3) {
            // Create date in local timezone to avoid one day less issue
            this.editedEndDate = new Date(parseInt(endDateParts[0]), parseInt(endDateParts[1]) - 1, parseInt(endDateParts[2]));
          } else {
            this.editedEndDate = new Date(this.project.endDate);
          }
        }
      }, 100);
    }
  }

  loadProjectDetails() {
    if (!this.project?.id) {
      return;
    }

    this.isLoading = true;
    
    // Fetch the latest project data with calculated ActualEffortDays and RemainingEffortDays
    this.projectService.getProjectById(this.project.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (project) => {
        this.project = project;
        this.initializeProject();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading project details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load project details. Please try again.'
        });
        // Fallback to using the project passed as input
        this.initializeProject();
        this.isLoading = false;
      }
    });
  }

  onVisibleChange(value: boolean) {
    if (!value && this.checklistHasUnsavedChanges) {
      this.confirmationService.confirm({
        message: 'You have unsaved checklist changes. Save your work first or leave without saving?',
        header: 'Unsaved Changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Save',
        rejectLabel: 'Leave',
        acceptButtonStyleClass: 'p-button-success',
        accept: () => {
          this.pendingClose = true;
          this.checklistComponent?.save();
        },
        reject: (type: ConfirmEventType) => {
          if (type === ConfirmEventType.REJECT) {
            this._doClose();
          }
          // ConfirmEventType.CANCEL = X button — just dismiss the confirm, keep project open
        }
      });
      return;
    }
    this.visibleChange.emit(value);
    if (!value) {
      this.close.emit();
      if (this.project) this.initializeProject();
    }
  }

  private _doClose(): void {
    this.checklistHasUnsavedChanges = false;
    this.pendingClose = false;
    this.visibleChange.emit(false);
    this.close.emit();
    if (this.project) this.initializeProject();
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';
    // Fix timezone issue by parsing date string properly
    const dateParts = dateString.split('-');
    if (dateParts.length === 3) {
      const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  formatDateForInput(dateString?: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  getStatusSeverity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (!status) return 'secondary';
    const statusLower = status.toLowerCase();
    if (statusLower === 'green') return 'success';
    if (statusLower === 'amber' || statusLower === 'yellow') return 'warn';
    if (statusLower === 'red') return 'danger';
    return 'secondary';
  }

  getProgressSeverity(progress?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (!progress) return 'secondary';
    const progressLower = progress.toLowerCase();
    // To start - Gray (secondary)
    if (progressLower.includes('to start')) return 'secondary';
    // On track - Green (success)
    if (progressLower.includes('on track')) return 'success';
    // Closed - Green (success)
    if (progressLower.includes('closed')) return 'success';
    // Off track - Amber (warn)
    if (progressLower.includes('off track')) return 'warn';
    // Blocker - Red (danger)
    if (progressLower.includes('blocker')) return 'danger';
    // On Hold - Red (danger)
    if (progressLower.includes('on hold')) return 'danger';
    return 'secondary';
  }
  
  getProgressBadgeClass(progress?: string): string {
    if (!progress) return 'status-secondary';
    const progressLower = progress.toLowerCase();
    // To start - Gray
    if (progressLower.includes('to start')) return 'status-secondary';
    // On track - Green
    if (progressLower.includes('on track')) return 'progress-success';
    // Closed - Green
    if (progressLower.includes('closed')) return 'progress-success';
    // Off track - Amber
    if (progressLower.includes('off track')) return 'progress-warn';
    // Blocker - Red
    if (progressLower.includes('blocker')) return 'progress-danger';
    // On Hold - Red
    if (progressLower.includes('on hold')) return 'progress-danger';
    return 'status-secondary';
  }
  

  saveProject() {
    if (!this.editedProject || !this.project) {
      return;
    }

    this.isSaving = true;

    // Prepare the project for update
    // Convert Date objects back to string format (YYYY-MM-DD)
    // Fix timezone issue by using local date components
    let startDateString: string | undefined = undefined;
    if (this.editedStartDate) {
      const year = this.editedStartDate.getFullYear();
      const month = String(this.editedStartDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.editedStartDate.getDate()).padStart(2, '0');
      startDateString = `${year}-${month}-${day}`;
    }
    
    let endDateString: string | undefined = undefined;
    if (this.editedEndDate) {
      const year = this.editedEndDate.getFullYear();
      const month = String(this.editedEndDate.getMonth() + 1).padStart(2, '0');
      const day = String(this.editedEndDate.getDate()).padStart(2, '0');
      endDateString = `${year}-${month}-${day}`;
    }

    const projectToUpdate: Project = {
      ...this.editedProject,
      startDate: startDateString || this.editedProject.startDate,
      endDate: endDateString
    };

    this.projectService.updateProject(this.project.id, projectToUpdate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updatedProject) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Project updated successfully'
        });
        this.projectUpdated.emit(updatedProject);
        this.project = updatedProject;
        this.initializeProject();
        this.isSaving = false;
        // Auto-close dialog after successful save
        this.onVisibleChange(false);
      },
      error: (error) => {
        console.error('Error updating project:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update project. Please try again.'
        });
        this.isSaving = false;
      }
    });
  }

  cancel() {
    this.onVisibleChange(false);
  }

  /**
   * Hands the open project to the Project Inventory, which owns the fuller edit form —
   * the fields this dialog shows read-only (code, client, managers) are editable there.
   *
   * Closes through {@link cancel} rather than navigating straight off, so the unsaved
   * checklist-changes guard still gets its say; the navigation only runs once the dialog
   * has actually closed. An in-flight save blocks the jump rather than racing it.
   */
  openInInventory(): void {
    if (this.isSaving || !this.editedProject) return;

    const projectId = this.editedProject.id;
    this.cancel();

    // Deferred a tick: cancel() may raise the unsaved-changes confirm, and navigating in
    // the same turn would tear the dialog down underneath it.
    setTimeout(() => {
      if (this.checklistHasUnsavedChanges) return;
      this.router.navigate(['/projects-inventory'], {
        queryParams: { projectId, openForm: 1 }
      });
    });
  }

  onChecklistDirtyChange(dirty: boolean): void {
    this.checklistHasUnsavedChanges = dirty;
    if (!dirty && this.pendingClose) {
      this._doClose();
    }
    if (!dirty && this.pendingTab) {
      this.activeTab = this.pendingTab;
      this.pendingTab = null;
    }
  }

  private pendingTab: string | null = null;

  setActiveTab(tab: string) {
    if (this.checklistHasUnsavedChanges && this.activeTab === 'process-checklist' && tab !== 'process-checklist') {
      this.confirmationService.confirm({
        message: 'You have unsaved checklist changes. Save your work first or leave without saving?',
        header: 'Unsaved Changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Save',
        rejectLabel: 'Leave',
        acceptButtonStyleClass: 'p-button-success',
        accept: () => {
          this.pendingTab = tab;
          this.checklistComponent?.save();
        },
        reject: (type: ConfirmEventType) => {
          if (type === ConfirmEventType.REJECT) {
            this.checklistHasUnsavedChanges = false;
            this.activeTab = tab;
          }
        }
      });
      return;
    }
    this.activeTab = tab;
  }

  // ─── Weekly Progress Report ───────────────────────────────────────────────

  private formatReportDate(date: Date): string {
    const d = date.getDate().toString().padStart(2, '0');
    const m = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  }

  private formatDisplayDate(dateStr?: string): string {
    if (!dateStr) return 'N/A';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const dt = new Date(+parts[0], +parts[1] - 1, +parts[2]);
      return dt.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return dateStr;
  }

  downloadWeeklyReportWord(): void {
    const p = this.editedProject!;
    if (!p?.id) return;

    forkJoin({
      current: this.weekPlanTaskService.getByProjectId(p.id).pipe(catchError(() => of([]))),
      next: this.nextWeekPlanTaskService.getByProjectId(p.id).pipe(catchError(() => of([]))),
      milestones: this.milestonePlanTaskService.getByProjectId(p.id).pipe(catchError(() => of([]))),
      raid: this.raidItemService.getByProjectId(p.id).pipe(catchError(() => of([])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ current, next, milestones, raid }) =>
        this.generateWordDoc(
          p,
          current.filter((t: any) => (t.status || '').toLowerCase().trim() === 'completed').map((t: any) => t.task),
          next.map((t: any) => t.task),
          milestones as any[],
          raid as any[]
        )
    });
  }

  private async generateWordDoc(
    p: Project,
    achievements: string[],
    nextWeek: string[],
    milestones: MilestonePlanTask[],
    raidItems: RaidItem[]
  ): Promise<void> {
    const dateStr = this.formatReportDate(new Date());

    try {
      const templateResponse = await fetch('templates/report-template.docx');
      if (!templateResponse.ok) throw new Error('Template file not found');
      const templateBuffer = await templateResponse.arrayBuffer();

      const { default: PizZip } = await import('pizzip') as any;
      const zip = new PizZip(templateBuffer);

      // ── Patch cover-page placeholders ────────────────────────────────────
      let xml: string = zip.file('word/document.xml').asText();

      const esc = (s: string) => s
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

      xml = xml.replace(/&lt;&lt;Project Name&gt;&gt;/g, esc(p.name || ''));
      xml = xml.replace(/&lt;&lt;Client Logo&gt;&gt;/g, esc(p.clientName || ''));
      xml = xml.replace(/&lt;&lt;Add overall comment&gt;&gt;/g, esc(p.progress || ''));
      xml = xml.replace(/DD-MMM-YYYY/g, esc(dateStr));

      // ── Patch landscape header placeholders (header4.xml) ────────────────
      let hdrXml: string = zip.file('word/header4.xml').asText();
      hdrXml = hdrXml.replace(/&lt;&lt;Project name&gt;&gt;/g, esc(p.name || ''));
      hdrXml = hdrXml.replace(/&lt;&lt;DD-MMM-YYYY&gt;&gt;/g, esc(dateStr));
      zip.file('word/header4.xml', hdrXml);

      // ── Convert Section 2 from landscape to portrait ──────────────────────
      xml = xml.replace(
        'w:w="16838" w:h="11906" w:orient="landscape" w:code="9"',
        'w:w="11906" w:h="16838"'
      );

      // ── Build content XML and inject into Section 2 ───────────────────────
      const contentXml = await this.buildReportContentXml(
        p, achievements, nextWeek, milestones, raidItems, dateStr
      );

      const S2_CONTENT_ID = '252FE48E';
      const S2_END_ID     = '541FAF5D';
      const s2ContentPos  = xml.indexOf(`paraId="${S2_CONTENT_ID}"`);
      const s2EndPos      = xml.indexOf(`paraId="${S2_END_ID}"`);

      if (s2ContentPos !== -1 && s2EndPos !== -1) {
        const insertBefore = xml.lastIndexOf('<w:p ', s2ContentPos);
        const insertEnd    = xml.lastIndexOf('<w:p ', s2EndPos);
        if (insertBefore !== -1 && insertEnd > insertBefore) {
          xml = xml.slice(0, insertBefore) + contentXml + xml.slice(insertEnd);
        }
      }

      // ── Save ──────────────────────────────────────────────────────────────
      zip.file('word/document.xml', xml);
      const output = zip.generate({ type: 'arraybuffer', compression: 'DEFLATE' }) as ArrayBuffer;
      const blob = new Blob([output], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const safeName = (p.name || 'Project').replace(/[^a-zA-Z0-9_-]/g, '_');
      saveAs(blob, `Weekly_Progress_Report_${safeName}_${dateStr}.docx`);
      this.messageService.add({ severity: 'success', summary: 'Downloaded', detail: 'Word document saved.' });

    } catch (err) {
      console.error('Report generation failed:', err);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to generate report.' });
    }
  }

  private async buildReportContentXml(
    p: Project,
    achievements: string[],
    nextWeek: string[],
    milestones: MilestonePlanTask[],
    raidItems: RaidItem[],
    dateStr: string
  ): Promise<string> {
    const blockers = raidItems
      .filter(r => (r.status || '').toLowerCase().trim() !== 'closed')
      .map(r => r.description);

    const DARK_BLUE = '1F3864';
    const cellBorder = { style: BorderStyle.SINGLE, size: 8, color: 'AAAAAA' };
    const borders = { top: cellBorder, bottom: cellBorder, left: cellBorder, right: cellBorder };

    const makeHeaderCell = (text: string, colSpan = 1): TableCell => new TableCell({
      children: [new Paragraph({
        children: [new TextRun({ text, bold: true, color: DARK_BLUE, size: 22, font: 'Calibri' })],
        spacing: { before: 60, after: 60 }
      })],
      columnSpan: colSpan, borders,
      margins: { top: 80, bottom: 80, left: 120, right: 120 }
    });

    const makeCell = (paras: Paragraph[], colSpan = 1): TableCell => new TableCell({
      children: paras, columnSpan: colSpan, borders,
      margins: { top: 80, bottom: 80, left: 120, right: 120 },
      verticalAlign: VerticalAlign.TOP
    });

    const makeLabelRow = (label: string, value: string): Paragraph => new Paragraph({
      children: [
        new TextRun({ text: `${label}  :  `, bold: true, size: 20, font: 'Calibri', color: '2C3E50' }),
        new TextRun({ text: value, size: 20, font: 'Calibri' })
      ],
      spacing: { before: 30, after: 30 }
    });

    const makeLegendRow = (label: string, desc: string, color?: string): Paragraph => new Paragraph({
      children: [
        new TextRun({ text: label, bold: true, size: 20, font: 'Calibri', color: color || '2C3E50' }),
        new TextRun({ text: `  :  ${desc}`, size: 20, font: 'Calibri' })
      ],
      spacing: { before: 20, after: 20 }
    });

    const makeListItem = (i: number, text: string): Paragraph => new Paragraph({
      children: [new TextRun({ text: `${i + 1}.  ${text || ''}`, size: 20, font: 'Calibri' })],
      spacing: { before: 30, after: 30 }
    });

    const formatDateObj = (d: Date | null): string =>
      d ? d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';

    const summaryCell = makeCell([
      makeLabelRow('Project Name', p.name || ''),
      makeLabelRow('Project Start Date', formatDateObj(this.editedStartDate)),
      makeLabelRow('Project End Date', formatDateObj(this.editedEndDate)),
      makeLabelRow('Overall Project Status', p.progress || ''),
      makeLabelRow('Overall Comments', p.progress || '')
    ]);

    const legendCell = makeCell([
      makeLegendRow('To start', 'Task to be started as planned'),
      makeLegendRow('On track', 'Task is in progress and can be completed as planned', '00B050'),
      makeLegendRow('Off track', 'Task is having challenges on meeting deadline', 'FF8C00'),
      makeLegendRow('Blocker', 'Having challenges and will be missing the deadline', 'FF0000'),
      makeLegendRow('OnHold', 'Task is on hold', 'FF8C00'),
      makeLegendRow('Completed', 'Task is completed', '00B050')
    ]);

    const achieveCell = makeCell(
      (achievements.length ? achievements : ['']).map((t, i) => makeListItem(i, t))
    );
    const nextWeekCell = makeCell(
      (nextWeek.length ? nextWeek : ['']).map((t, i) => makeListItem(i, t))
    );
    const blockerItems = (blockers.length ? blockers : ['', '', '']).map((t, i) => makeListItem(i, t));

    const formatMsDate = (ds?: string): string => {
      if (!ds) return '-';
      const parts = ds.split('T')[0].split('-');
      if (parts.length === 3) {
        return new Date(+parts[0], +parts[1] - 1, +parts[2])
          .toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
      }
      return ds;
    };

    const msBorder = { style: BorderStyle.SINGLE, size: 6, color: 'BBBBBB' };
    const msBorders = { top: msBorder, bottom: msBorder, left: msBorder, right: msBorder };

    const makeMsHdr = (text: string, w: number): TableCell => new TableCell({
      children: [new Paragraph({
        children: [new TextRun({ text, bold: true, color: DARK_BLUE, size: 18, font: 'Calibri' })],
        spacing: { before: 40, after: 40 }
      })],
      borders: msBorders, width: { size: w, type: WidthType.PERCENTAGE },
      margins: { top: 60, bottom: 60, left: 100, right: 100 }
    });

    const makeMsCell = (text: string, w: number): TableCell => new TableCell({
      children: [new Paragraph({
        children: [new TextRun({ text: text || '', size: 18, font: 'Calibri' })],
        spacing: { before: 30, after: 30 }
      })],
      borders: msBorders, width: { size: w, type: WidthType.PERCENTAGE },
      margins: { top: 60, bottom: 60, left: 100, right: 100 },
      verticalAlign: VerticalAlign.TOP
    });

    const getMsColor = (status: string): string => {
      const s = (status || '').toLowerCase().trim();
      if (s === 'completed' || s === 'on track') return '00B050';
      if (s === 'off track' || s === 'on hold') return 'FF8C00';
      if (s === 'blocker') return 'FF0000';
      return '444444';
    };

    const makeMsStatusCell = (status: string, w: number): TableCell => new TableCell({
      children: [new Paragraph({
        children: [new TextRun({ text: status || '', size: 18, font: 'Calibri', bold: true, color: getMsColor(status) })],
        spacing: { before: 30, after: 30 }
      })],
      borders: msBorders, width: { size: w, type: WidthType.PERCENTAGE },
      margins: { top: 60, bottom: 60, left: 100, right: 100 },
      verticalAlign: VerticalAlign.TOP
    });

    const milestoneRows: TableRow[] = [
      new TableRow({ children: [new TableCell({
        children: [new Paragraph({
          children: [new TextRun({ text: 'MILESTONE PLAN', bold: true, color: DARK_BLUE, size: 22, font: 'Calibri' })],
          spacing: { before: 60, after: 60 }
        })],
        columnSpan: 6, borders: msBorders,
        margins: { top: 80, bottom: 80, left: 120, right: 120 }
      })] }),
      new TableRow({ children: [
        makeMsHdr('#', 5), makeMsHdr('Task Name', 30), makeMsHdr('Start Date', 15),
        makeMsHdr('Target Date', 15), makeMsHdr('Status', 15), makeMsHdr('Comments', 20)
      ]}),
      ...(milestones.length
        ? milestones.map((m, idx) => new TableRow({ children: [
            makeMsCell(String(idx + 1), 5), makeMsCell(m.task || '', 30),
            makeMsCell(formatMsDate(m.startDate), 15), makeMsCell(formatMsDate(m.targetDate), 15),
            makeMsStatusCell(m.status || '', 15), makeMsCell(m.comments || '', 20)
          ]}))
        : [new TableRow({ children: [
            makeMsCell('', 5), makeMsCell('No milestone data available', 30),
            makeMsCell('', 15), makeMsCell('', 15), makeMsCell('', 15), makeMsCell('', 20)
          ]})]
      )
    ];

    const contentDoc = new Document({
      sections: [{
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: 'Weekly Project Progress Report  ', bold: true, size: 28, font: 'Calibri', color: DARK_BLUE }),
              new TextRun({ text: dateStr, bold: true, size: 28, font: 'Calibri', color: 'C00000' })
            ],
            spacing: { before: 100, after: 200 }
          }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              new TableRow({ children: [makeHeaderCell('PROJECT SUMMARY'), makeHeaderCell('LEGEND')] }),
              new TableRow({ children: [summaryCell, legendCell] }),
              new TableRow({ children: [makeHeaderCell('CURRENT WEEK ACHIEVEMENTS'), makeHeaderCell('PLAN FOR NEXT WEEK')] }),
              new TableRow({ children: [achieveCell, nextWeekCell] }),
              new TableRow({ children: [makeHeaderCell('CHALLENGERS / BLOCKERS', 2)] }),
              new TableRow({ children: [makeCell(blockerItems, 2)] })
            ]
          }),
          new Paragraph({ text: '', spacing: { before: 240, after: 120 } }),
          new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: milestoneRows })
        ]
      }]
    });

    const contentBlob = await Packer.toBlob(contentDoc);
    const contentBuffer = await contentBlob.arrayBuffer();
    const { default: PizZip } = await import('pizzip') as any;
    const cZip = new PizZip(contentBuffer);
    const cXml: string = cZip.file('word/document.xml').asText();
    const bodyStart = cXml.indexOf('<w:body>') + '<w:body>'.length;
    const sectPrStart = cXml.lastIndexOf('<w:sectPr');
    return cXml.slice(bodyStart, sectPrStart > bodyStart ? sectPrStart : undefined);
  }

  /** Actual Effort (Days) from timesheet hours – displayed as whole number, no decimals. */
  getActualEffortDisplay(): string {
    const val = this.editedProject?.actualEffortDays;
    if (val == null || isNaN(val)) return '0';
    return Math.round(val).toString();
  }

  getRemainingEffort(): string {
    if (!this.editedProject?.plannedEstimateDays) {
      return '0';
    }
    const actualEffort = this.editedProject.actualEffortDays || 0;
    const remaining = this.editedProject.plannedEstimateDays - actualEffort;
    return Math.round(remaining).toString();
  }
}

